import icn_linebar_chart from "assets/images/icons/icn_linebar_chart.svg";
import icn_info_solid from "assets/images/icons/icn_Info_solid.svg";
import { ReactComponent as Clock } from "assets/images/icons/icn_display_clock.svg";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Tooltip,
  tooltipClasses,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { SolidButton } from "components/buttons/SolidButton";

import WirelessChannelInterfaceChart from "../../charts/WirelessChannelInterfaceChart";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#4D4D4D",
    color: theme.palette.primary.white,
    maxWidth: 600,
    fontSize: theme.typography.pxToRem(12),
    padding: "16px 20px",
    borderRadius: "8px",
    lineHeight: "1.5",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
  },
}));

const ActionList = ({ items }) => (
  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ mb: 0.5 }}>
        {item}
      </Box>
    ))}
  </Box>
);

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Good — Noise Floor / Interference &lt; -70 dBm
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        The wireless channel is clean with minimal contention for airtime.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Warning — -70 dBm ≤ Interference ≤ -60 dBm
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        Competing signals have been detected. Users may see slightly higher
        latency during heavy traffic periods.
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        <ActionList
          items={[
            "· Check the Neighboring AP Count for new nearby access points.​",
            "· Ensure Dynamic Channel Assignment (Auto-RF) is active.​",
          ]}
        />
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Critical — Interference &gt; -60 dBm
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        Strong neighboring signals are consuming airtime, causing packet retries
        and choppy performance.
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        <ActionList
          items={[
            "· Move critical traffic to the 5GHz/6GHz bands and re-plan channels.​",
            "· Reduce channel width and tune transmit power to limit overlap.​",
          ]}
        />
      </Typography>
    </Box>
  </Box>
);

const WirelessChannelInterfaceAndNeighboringAPCount = ({
  onClick,
  loading = false,
  noData = false,
  wirelessChannelInterfaceChartSeriesData = [],
  wirelessChannelInterfaceApiChartData,
  wirelessChannelInterfaceApList,
  radioData,
  status,
  statusColorMap,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const isNoData = noData || Number(wirelessChannelInterfaceApiChartData?.noisyAps) === 0;
  const isNeighborApDisabled = !noData && !radioData?.["wireless.radioGlobal"]?.neighborApDetection;

  const formatLastScanTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return new Intl.DateTimeFormat("zh-TW", {
      timeZone: "Asia/Taipei",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };

  const formatted = formatLastScanTime(
    wirelessChannelInterfaceApiChartData?.latestTs,
  );
  const noisySSIDsTotal = wirelessChannelInterfaceChartSeriesData.reduce(
    (sum, item) => sum + item[1],
    0,
  );

  return (
    <Box
      onClick={isNoData || isNeighborApDisabled ? undefined : onClick}
      sx={{
        border: `1px solid ${theme.palette.primary.greyThread}`,
        borderRadius: theme.shape.customBorderRadius[8],
        p: 2.5,
        bgcolor: theme.palette.primary.white,
        mb: 3,
        cursor: onClick && !isNoData ? "pointer" : "default",
        "&:hover":
          onClick && !isNoData
            ? {
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
              }
            : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={icn_linebar_chart}
            sx={{ width: "18px", height: "18px", mr: 1 }}
          />

          <Typography
            sx={{
              fontWeight: theme.typography.customFontWeight[2],
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[3],
              mr: 1,
            }}
          >
            Wireless Channel Interference
          </Typography>

          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
          </HtmlTooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last Scan : {formatted}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            height: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : isNeighborApDisabled ? (
        <Box
          sx={{
            minHeight: "304px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography
              sx={{
                fontWeight: theme.typography.customFontWeight[1],
                color: theme.palette.primary.greyThird,
                fontSize: theme.typography.customFontSize[3],
                mb: 1
              }}
            >
              Neighbor AP Detection Required
            </Typography>
            <Typography
              sx={{
                color: theme.palette.primary.greyThird,
                fontSize: theme.typography.customFontSize[2],
                mb: 2
              }}
            >
              Neighbor AP Detection is currently disabled. Turn on this feature to monitor surrounding access points.
            </Typography>

            <SolidButton
              width="155px"
              height="32px"
              onClick={() => navigate("/site/settings", { state: { navigateTab: "wireless-radio" } })}
            >
              Go to Settings
            </SolidButton>
          </Box>
        </Box>
      ) : isNoData ? (
        <Box
          sx={{
            height: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.greyThird,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            No data available
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: 280 },
              pr: { xs: 0, lg: 5 },
              pb: { xs: 3, lg: 0 },
              borderRight: {
                xs: "none",
                lg: `1px solid ${theme.palette.primary.greyThread}`,
              },
            }}
          >
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.greyPrimary,
                  mb: 0.5,
                }}
              >
                Status
              </Typography>

              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[6],
                  lineHeight: 1.1,
                  fontWeight: theme.typography.customFontWeight[3],
                  color: statusColorMap[status],
                }}
              >
                {status}
              </Typography>
            </Box>

            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.primary.greyThread}`,
                pt: 2.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  mb: 1,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Noisy SSIDs:{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {noisySSIDsTotal}
                </Box>
              </Typography>

              <Typography
                sx={{
                  mb: 1,
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Average Noise:{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {wirelessChannelInterfaceApiChartData.avgRssi} dBm
                </Box>
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              pl: { xs: 0, lg: 5 },
              pt: { xs: 1, lg: 0 },
            }}
          >
            <WirelessChannelInterfaceChart
              data={wirelessChannelInterfaceChartSeriesData}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WirelessChannelInterfaceAndNeighboringAPCount;
